/**
 * catanCoord.ts — 카탄 보드 좌표 헬퍼 (Phase 3+ 재설계 2026-04-30)
 *
 * 알고리즘: 픽셀 dedup 기반 결정론적 ID 부여
 *   1) 19헥스(HEX_MASTER) × 6 corner 픽셀 산출
 *   2) 픽셀 dedup(eps=1e-6) → vertex 54개
 *   3) 19헥스 × 6 side → edge dedup → 72개
 *   4) 외곽/내부 분리: reps.length < 3 외곽(30) / =3 내부(24)
 *   5) 외곽 시계방향 순환(시작 = argmin(y,x), 두 후보면 x 큰 쪽) → vertex_id 1~30
 *   6) 내부 (y asc, x asc) → vertex_id 31~54
 *   7) edge_id: (min(v1.id, v2.id), max(...)) lexicographic 정렬 → 1~72
 *
 * 결정론: 부동소수 산술만 사용 → IEEE 754 동일 → C# CatanCoord.cs 와 동일 결과
 *
 * 사용 가능 픽셀 단위는 hexSize=1 기준의 unit 좌표. getVertexPixel/getEdgePixel에서
 * hexSize·centerX·centerY로 스케일·오프셋.
 *
 * SP 정합: HEX_MASTER 순서는 sp_CatanBoardInit tmp_hex_master 와 일치(상단 r=-2 → 하단 r=+2)
 */

// ── 헥스 마스터 (HEX_ID 1-based, index 0 = HEX_ID 1) ────────────────────
// SP tmp_hex_master 와 동일 순서: 위(r=-2) → 아래(r=+2)
const HEX_MASTER: Array<{ q: number; r: number }> = [
  { q:  0, r: -2 }, { q:  1, r: -2 }, { q:  2, r: -2 },                                           // HEX  1~ 3 (r=-2)
  { q: -1, r: -1 }, { q:  0, r: -1 }, { q:  1, r: -1 }, { q:  2, r: -1 },                          // HEX  4~ 7 (r=-1)
  { q: -2, r:  0 }, { q: -1, r:  0 }, { q:  0, r:  0 }, { q:  1, r:  0 }, { q:  2, r:  0 },        // HEX  8~12 (r= 0)
  { q: -2, r:  1 }, { q: -1, r:  1 }, { q:  0, r:  1 }, { q:  1, r:  1 },                          // HEX 13~16 (r= 1)
  { q: -2, r:  2 }, { q: -1, r:  2 }, { q:  0, r:  2 },                                           // HEX 17~19 (r= 2)
]

const VALID_HEX_SET = new Set<string>(HEX_MASTER.map(h => `${h.q},${h.r}`))
const HEX_QR_TO_ID = new Map<string, number>()
HEX_MASTER.forEach((h, i) => HEX_QR_TO_ID.set(`${h.q},${h.r}`, i + 1))

export function isValidHex(q: number, r: number): boolean {
  return VALID_HEX_SET.has(`${q},${r}`)
}

const EPS = 1e-6
const SQRT3 = Math.sqrt(3)

// ── 단위 좌표(hexSize=1) 산출 ──────────────────────────────────────────
function hexCenterUnit(q: number, r: number): { x: number; y: number } {
  return { x: SQRT3 * q + (SQRT3 / 2) * r, y: 1.5 * r }
}

function hexCornerUnit(hcx: number, hcy: number, corner: number): { x: number; y: number } {
  const ang = (Math.PI / 180) * (60 * corner - 30)
  return { x: hcx + Math.cos(ang), y: hcy + Math.sin(ang) }
}

// ── 빌드: vertex / edge / 인접 / id 매핑 ───────────────────────────────
type VertRec = { x: number; y: number; reps: Array<{ hexId: number; c: number }> }
type EdgeRec = { v1Idx: number; v2Idx: number; reps: Array<{ hexId: number; s: number }> }

function build(): {
  verts: VertRec[]
  edges: EdgeRec[]
  ringIdx: number[]              // outer cycle, 30 elements (idx in verts[])
  innerIdx: number[]             // inner sorted, 24 elements
  idxToId: Map<number, number>   // verts[] idx → vertex id (1..54)
  idToIdx: Map<number, number>   // vertex id → verts[] idx
  edgesWithIds: Array<{ id: number; v1: number; v2: number }>  // sorted by (v1,v2) lex
  edgeIdToVerts: Map<number, [number, number]>                 // edge id → [vid1, vid2] (sorted asc)
  vertexUnitById: Map<number, { x: number; y: number }>        // vertex id → unit pixel
} {
  // 1) vertex dedup
  const verts: VertRec[] = []
  function findOrCreate(x: number, y: number): number {
    for (let i = 0; i < verts.length; i++) {
      if (Math.hypot(verts[i].x - x, verts[i].y - y) < EPS) return i
    }
    verts.push({ x, y, reps: [] })
    return verts.length - 1
  }
  for (let h = 0; h < HEX_MASTER.length; h++) {
    const { q, r } = HEX_MASTER[h]
    const c0 = hexCenterUnit(q, r)
    for (let c = 0; c < 6; c++) {
      const p = hexCornerUnit(c0.x, c0.y, c)
      const idx = findOrCreate(p.x, p.y)
      verts[idx].reps.push({ hexId: h + 1, c })
    }
  }

  // 2) edge dedup
  const edges: EdgeRec[] = []
  const edgeKeyMap = new Map<string, number>()
  function eKey(a: number, b: number): string {
    return a < b ? `${a}|${b}` : `${b}|${a}`
  }
  function findOrCreateEdge(v1Idx: number, v2Idx: number): number {
    const k = eKey(v1Idx, v2Idx)
    const cached = edgeKeyMap.get(k)
    if (cached !== undefined) return cached
    edges.push({ v1Idx, v2Idx, reps: [] })
    const idx = edges.length - 1
    edgeKeyMap.set(k, idx)
    return idx
  }
  function vertIndexAt(p: { x: number; y: number }): number {
    for (let i = 0; i < verts.length; i++) {
      if (Math.hypot(verts[i].x - p.x, verts[i].y - p.y) < EPS) return i
    }
    return -1
  }
  for (let h = 0; h < HEX_MASTER.length; h++) {
    const { q, r } = HEX_MASTER[h]
    const c0 = hexCenterUnit(q, r)
    for (let s = 0; s < 6; s++) {
      const p1 = hexCornerUnit(c0.x, c0.y, s)
      const p2 = hexCornerUnit(c0.x, c0.y, (s + 1) % 6)
      const a = vertIndexAt(p1)
      const b = vertIndexAt(p2)
      const eIdx = findOrCreateEdge(a, b)
      edges[eIdx].reps.push({ hexId: h + 1, s })
    }
  }

  // 3) outer / inner
  const outerIdx: number[] = []
  const innerIdx: number[] = []
  verts.forEach((v, i) => {
    if (v.reps.length < 3) outerIdx.push(i)
    else innerIdx.push(i)
  })

  // 4) outer 그래프 인접 (외곽 vertex끼리 외곽 edge로 연결, 외곽 edge = reps.length<2)
  const outerSet = new Set<number>(outerIdx)
  const adjOuter = new Map<number, number[]>()
  for (const e of edges) {
    if (outerSet.has(e.v1Idx) && outerSet.has(e.v2Idx) && e.reps.length < 2) {
      if (!adjOuter.has(e.v1Idx)) adjOuter.set(e.v1Idx, [])
      if (!adjOuter.has(e.v2Idx)) adjOuter.set(e.v2Idx, [])
      adjOuter.get(e.v1Idx)!.push(e.v2Idx)
      adjOuter.get(e.v2Idx)!.push(e.v1Idx)
    }
  }

  // 5) outer 시계방향 순환 — 시작점 = argmin(y, x)
  let startIdx = -1
  for (const i of outerIdx) {
    if (startIdx === -1) { startIdx = i; continue }
    const a = verts[i], b = verts[startIdx]
    if (a.y < b.y - EPS || (Math.abs(a.y - b.y) < EPS && a.x < b.x)) startIdx = i
  }
  const ringIdx: number[] = []
  let prev = -1, cur = startIdx
  for (let i = 0; i < outerIdx.length; i++) {
    ringIdx.push(cur)
    const prevSnap = prev
    const curSnap  = cur
    const candidates = (adjOuter.get(curSnap) || []).filter(n => n !== prevSnap)
    let next: number
    if (candidates.length === 1) next = candidates[0]
    else {
      // 두 후보 중 x가 큰 쪽 (시작점이 최상단/좌측이므로 시계방향 = 오른쪽 진행)
      candidates.sort((x, y) => verts[y].x - verts[x].x)
      next = candidates[0]
    }
    prev = curSnap
    cur = next
  }

  // 6) 내부 정렬 (y asc, x asc)
  const innerSorted = innerIdx.slice().sort((a, b) => {
    const A = verts[a], B = verts[b]
    if (Math.abs(A.y - B.y) > EPS) return A.y - B.y
    return A.x - B.x
  })

  // 7) ID 부여
  const idxToId = new Map<number, number>()
  ringIdx.forEach((idx, i) => idxToId.set(idx, i + 1))
  innerSorted.forEach((idx, i) => idxToId.set(idx, 31 + i))

  const idToIdx = new Map<number, number>()
  idxToId.forEach((id, idx) => idToIdx.set(id, idx))

  // 8) edge id: (min(v1id, v2id), max) lex sort
  const edgesWithIds = edges.map(e => {
    const a = idxToId.get(e.v1Idx)!
    const b = idxToId.get(e.v2Idx)!
    return { id: 0, v1: Math.min(a, b), v2: Math.max(a, b) }
  })
  edgesWithIds.sort((x, y) => x.v1 - y.v1 || x.v2 - y.v2)
  edgesWithIds.forEach((e, i) => (e.id = i + 1))

  const edgeIdToVerts = new Map<number, [number, number]>()
  edgesWithIds.forEach(e => edgeIdToVerts.set(e.id, [e.v1, e.v2]))

  // 9) 픽셀 unit 매핑 (vertex id → dedup 픽셀)
  const vertexUnitById = new Map<number, { x: number; y: number }>()
  for (let id = 1; id <= 54; id++) {
    const idx = idToIdx.get(id)!
    vertexUnitById.set(id, { x: verts[idx].x, y: verts[idx].y })
  }

  return { verts, edges, ringIdx, innerIdx: innerSorted, idxToId, idToIdx, edgesWithIds, edgeIdToVerts, vertexUnitById }
}

const _BUILD = build()

// ── 인접 관계 빌드 (id 기반) ──────────────────────────────────────────

function buildVAdjVertices(): Record<number, number[]> {
  const r: Record<number, number[]> = {}
  for (let v = 1; v <= 54; v++) r[v] = []
  for (const e of _BUILD.edgesWithIds) {
    r[e.v1].push(e.v2)
    r[e.v2].push(e.v1)
  }
  for (let v = 1; v <= 54; v++) r[v].sort((a, b) => a - b)
  return r
}

function buildVAdjEdges(): Record<number, number[]> {
  const r: Record<number, number[]> = {}
  for (let v = 1; v <= 54; v++) r[v] = []
  for (const e of _BUILD.edgesWithIds) {
    r[e.v1].push(e.id)
    r[e.v2].push(e.id)
  }
  for (let v = 1; v <= 54; v++) r[v].sort((a, b) => a - b)
  return r
}

function buildVAdjHexes(): Record<number, number[]> {
  const r: Record<number, number[]> = {}
  for (let v = 1; v <= 54; v++) {
    const idx = _BUILD.idToIdx.get(v)!
    const set = new Set<number>()
    for (const rep of _BUILD.verts[idx].reps) set.add(rep.hexId)
    r[v] = Array.from(set).sort((a, b) => a - b)
  }
  return r
}

function buildEAdjVertices(): Record<number, number[]> {
  const r: Record<number, number[]> = {}
  for (let e = 1; e <= 72; e++) {
    const [a, b] = _BUILD.edgeIdToVerts.get(e)!
    r[e] = [a, b]
  }
  return r
}

function buildEAdjEdges(eAdjV: Record<number, number[]>, vAdjE: Record<number, number[]>): Record<number, number[]> {
  const r: Record<number, number[]> = {}
  for (let e = 1; e <= 72; e++) {
    const set = new Set<number>()
    for (const v of eAdjV[e]) {
      for (const ae of vAdjE[v]) if (ae !== e) set.add(ae)
    }
    r[e] = Array.from(set).sort((a, b) => a - b)
  }
  return r
}

const _vAdjVertices = buildVAdjVertices()
const _vAdjEdges    = buildVAdjEdges()
const _vAdjHexes    = buildVAdjHexes()
const _eAdjVertices = buildEAdjVertices()
const _eAdjEdges    = buildEAdjEdges(_eAdjVertices, _vAdjEdges)

// ── 공개 인접 관계 Record (캐시) ──────────────────────────────────────
export const vertexAdjacentVertices: Record<number, number[]> = _vAdjVertices
export const vertexAdjacentEdges:    Record<number, number[]> = _vAdjEdges
export const vertexAdjacentHexes:    Record<number, number[]> = _vAdjHexes
export const edgeAdjacentVertices:   Record<number, number[]> = _eAdjVertices
export const edgeAdjacentEdges:      Record<number, number[]> = _eAdjEdges

// ── 공개 함수 ─────────────────────────────────────────────────────────

export function isValidVertexId(v: number): boolean {
  return Number.isInteger(v) && v >= 1 && v <= 54
}

export function isValidEdgeId(e: number): boolean {
  return Number.isInteger(e) && e >= 1 && e <= 72
}

export function getAdjacentVertices(vertexId: number): number[] {
  return _vAdjVertices[vertexId] ?? []
}

export function getAdjacentEdges(vertexId: number): number[] {
  return _vAdjEdges[vertexId] ?? []
}

export function getAdjacentHexes(vertexId: number): number[] {
  return _vAdjHexes[vertexId] ?? []
}

export function getEdgeVertices(edgeId: number): number[] {
  return _eAdjVertices[edgeId] ?? []
}

export function getEdgeAdjacentEdges(edgeId: number): number[] {
  return _eAdjEdges[edgeId] ?? []
}

// ── hex → 6 vertex 역매핑 (서버 CatanCoord.GetHexAdjacentVertices 미러) ──
// 사용처: 도적 약탈 victim 후보 산출 — 도둑 hex의 6 vertex 점유자만 후보 (2026-05-06 fix)
const _hexAdjVertices: Record<number, number[]> = (() => {
  const map: Record<number, number[]> = {}
  // _vAdjHexes (vertex → 인접 hex 1~3개) 를 invert
  for (const vidStr of Object.keys(_vAdjHexes)) {
    const vid = Number(vidStr)
    for (const hid of _vAdjHexes[vid]) {
      if (!map[hid]) map[hid] = []
      map[hid].push(vid)
    }
  }
  // 정렬 + 중복 제거
  for (const hid of Object.keys(map)) {
    map[Number(hid)] = Array.from(new Set(map[Number(hid)])).sort((a, b) => a - b)
  }
  return map
})()

/**
 * hex 1개의 6 vertex_id 반환 (서버 CatanCoord.GetHexAdjacentVertices 알고리즘 미러)
 * - 도둑 hex의 인접 vertex 점유자 산출에 사용 (2026-05-06 victim fix)
 * - 외곽 hex도 6개 모두 반환 (인접 hex가 없는 vertex도 본 hex가 인접하므로 포함)
 */
export function getHexVertices(hexId: number): number[] {
  return _hexAdjVertices[hexId] ?? []
}

// 단언: 19 hex × 6 vertex = 114 매핑 (각 vertex가 1~3개 hex에 속하므로 114 = 54+54+24-... 검증)
// 정확히는 hexId 1~19 각각 6 vertex 반환
;(function assertHexVertices() {
  for (let h = 1; h <= 19; h++) {
    const vs = _hexAdjVertices[h]
    if (!vs || vs.length !== 6) {
      // eslint-disable-next-line no-console
      console.error(`[catanCoord] hex ${h} adj vertices count = ${vs?.length ?? 0} (expected 6)`, vs)
    }
  }
})()

// ── 픽셀 좌표 (hexSize/centerX/centerY로 스케일·오프셋) ────────────────

export function getVertexPixel(
  vertexId: number,
  hexSize:  number,
  centerX:  number,
  centerY:  number
): { x: number; y: number } {
  const u = _BUILD.vertexUnitById.get(vertexId)
  if (!u) return { x: centerX, y: centerY }
  return { x: centerX + u.x * hexSize, y: centerY + u.y * hexSize }
}

export function getEdgePixel(
  edgeId:  number,
  hexSize: number,
  centerX: number,
  centerY: number
): { x1: number; y1: number; x2: number; y2: number } {
  const verts = _eAdjVertices[edgeId]
  if (!verts || verts.length !== 2) {
    return { x1: centerX, y1: centerY, x2: centerX, y2: centerY }
  }
  const p1 = getVertexPixel(verts[0], hexSize, centerX, centerY)
  const p2 = getVertexPixel(verts[1], hexSize, centerX, centerY)
  return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y }
}

// ── 검증 (모듈 로드 시 자체 단언) ─────────────────────────────────────
function runAssertions() {
  // 1) 개수
  console.assert(_BUILD.verts.length === 54,
    `[catanCoord] vertex 수 오류: ${_BUILD.verts.length} (expected 54)`)
  console.assert(_BUILD.edges.length === 72,
    `[catanCoord] edge 수 오류: ${_BUILD.edges.length} (expected 72)`)

  // 2) 모든 edge 양 끝점 vertex 정확히 2개
  for (let e = 1; e <= 72; e++) {
    const verts = _eAdjVertices[e]
    console.assert(verts && verts.length === 2,
      `[catanCoord] edge ${e} 인접 vertex 수 오류: ${verts?.length}`)
  }

  // 3) 외곽 30 / 내부 24 (reps 정의)
  console.assert(_BUILD.ringIdx.length === 30,
    `[catanCoord] 외곽 ring 길이 오류: ${_BUILD.ringIdx.length} (expected 30)`)
  console.assert(_BUILD.innerIdx.length === 24,
    `[catanCoord] 내부 vertex 수 오류: ${_BUILD.innerIdx.length} (expected 24)`)

  // 4) vertex degree(V↔V) 분포: 외곽 18 deg=2 + 그 외 36 deg=3
  let deg2 = 0, deg3 = 0
  for (let v = 1; v <= 54; v++) {
    const d = _vAdjVertices[v].length
    if (d === 2) deg2++
    else if (d === 3) deg3++
    else console.assert(false, `[catanCoord] vertex ${v} V↔V degree=${d} (expected 2 or 3)`)
  }
  console.assert(deg2 === 18 && deg3 === 36,
    `[catanCoord] V↔V degree 분포 오류: deg2=${deg2} deg3=${deg3} (expected 18/36)`)

  // 5) vertex degree(V↔E) 모두 2 또는 3
  for (let v = 1; v <= 54; v++) {
    const d = _vAdjEdges[v].length
    console.assert(d === 2 || d === 3,
      `[catanCoord] vertex ${v} V↔E degree=${d} (expected 2 or 3)`)
  }

  // 6) 표준 항구 9쌍 모두 외곽 인접쌍 (간격 3,3,4 반복)
  const portStarts = [1, 4, 7, 11, 14, 17, 21, 24, 27]
  for (const a of portStarts) {
    const b = (a % 30) + 1
    const ok = _vAdjVertices[a].includes(b) && a <= 30 && b <= 30
    console.assert(ok, `[catanCoord] 표준 항구 외곽 인접쌍 (${a},${b}) 검증 실패`)
  }

  // 7) ID 1..54 / 1..72 모두 매핑 존재
  for (let v = 1; v <= 54; v++) {
    console.assert(_BUILD.idToIdx.has(v), `[catanCoord] vertex id ${v} 매핑 없음`)
    console.assert(_BUILD.vertexUnitById.has(v), `[catanCoord] vertex id ${v} 픽셀 매핑 없음`)
  }
  for (let e = 1; e <= 72; e++) {
    console.assert(_BUILD.edgeIdToVerts.has(e), `[catanCoord] edge id ${e} 매핑 없음`)
  }

  // 8) edge id 1~72 양 끝점 (v1<v2) 단조성
  for (let e = 1; e <= 72; e++) {
    const [a, b] = _BUILD.edgeIdToVerts.get(e)!
    console.assert(a < b, `[catanCoord] edge ${e} v1>=v2: (${a},${b})`)
  }

  console.log(
    `[catanCoord] 검증 통과 — vertex 54 / edge 72 / 외곽 ${_BUILD.ringIdx.length} / 내부 ${_BUILD.innerIdx.length}`
  )
}

runAssertions()
